/* global theme */

class LisavarustePicker extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.items = [];
  }

  connectedCallback() {
    this.parseConfig();
    this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    this.form = this.section?.querySelector('.js-product-form-main');
    this.list = this.querySelector('[data-lisavaruste-list]');
    this.items = [...this.querySelectorAll('[data-lisavaruste-item]')];
    this.block = this.closest('.lisavaruste-block');

    if (!this.items.length) {
      this.hideBlock();
      return;
    }

    this.optionKeys = new Set(
      this.items.map((item) => item.dataset.optionKey).filter(Boolean)
    );

    this.sortItems();
    this.bindEvents();
    this.bindVariantChange();
    this.syncQtyLabels();
    this.updateVisibility();
    this.refreshProductTotal();
  }

  hideBlock() {
    if (this.block) {
      this.block.hidden = true;
      this.block.classList.add('is-hidden');
    } else {
      this.hidden = true;
    }
  }

  showBlock() {
    if (this.block) {
      this.block.hidden = false;
      this.block.classList.remove('is-hidden');
    }
  }

  hasVisibleItems() {
    return this.items.some((item) => !item.hidden && !item.classList.contains('is-hidden'));
  }

  syncBlockVisibility() {
    if (this.hasVisibleItems()) {
      this.showBlock();
    } else {
      this.hideBlock();
    }
  }

  parseConfig() {
    const script = this.querySelector('[data-lisavaruste-config]');
    if (!script) return;

    try {
      this.config = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('lisavaruste: invalid config', error);
    }
  }

  sortItems() {
    this.items.sort(
      (a, b) => Number(a.dataset.sortOrder || 0) - Number(b.dataset.sortOrder || 0)
    );
    this.items.forEach((item) => this.list?.appendChild(item));
  }

  bindEvents() {
    this.list?.addEventListener('change', (event) => {
      const checkbox = event.target.closest('.checkbox');
      if (!checkbox) return;

      const item = checkbox.closest('[data-lisavaruste-item]');
      if (!item) return;

      if (item.dataset.groupName) {
        this.enforceExclusiveGroup(item);
      }

      this.updateVisibility();
    });

    this.form?.addEventListener('submit', this.handleSubmit.bind(this), true);

    // Delegated: the quantity input is re-rendered on every variant change.
    ['change', 'input'].forEach((type) => {
      this.section?.addEventListener(type, (event) => {
        if (!event.target.matches?.('.js-product-form-main input[name="quantity"]')) return;
        this.syncQtyLabels();
        this.refreshProductTotal();
      });
    });
  }

  bindVariantChange() {
    if (this._boundVariantChange) return;
    this._boundVariantChange = true;

    document.addEventListener('on:variant:change', (event) => {
      if (!this.section?.contains(event.target)) return;
      this.refreshProductTotal();
    });
  }

  refreshProductTotal() {
    if (!this.section) return;

    const hintaportaat = this.section.querySelector('hintaportaat-picker');
    if (hintaportaat && typeof hintaportaat.updateUI === 'function') {
      hintaportaat.updateUI(hintaportaat.getQuantity());
      return;
    }

    theme.hintaportaat?.updateProductTotalBar?.(this.section);
  }

  getCheckbox(item) {
    return item.querySelector('.checkbox');
  }

  isChecked(item) {
    const checkbox = this.getCheckbox(item);
    return Boolean(checkbox?.checked);
  }

  setChecked(item, checked, locked = false) {
    const checkbox = this.getCheckbox(item);
    if (!checkbox) return;

    checkbox.checked = checked;
    checkbox.disabled = locked;
    item.classList.toggle('is-locked', locked);
  }

  isOrphanShowWhen(showWhen) {
    return Boolean(showWhen) && !this.optionKeys.has(showWhen);
  }

  isTriggerActive(showWhen, item) {
    if (!showWhen) return true;
    if (item?.dataset.showWhenOrphan === 'true' || this.isOrphanShowWhen(showWhen)) {
      return true;
    }

    const triggerItem = this.items.find((entry) => entry.dataset.optionKey === showWhen);
    if (triggerItem) return this.isChecked(triggerItem);

    return false;
  }

  enforceExclusiveGroup(activeItem) {
    const groupName = activeItem.dataset.groupName;
    if (!groupName || !this.isChecked(activeItem)) return;

    this.items.forEach((item) => {
      if (item === activeItem || item.dataset.groupName !== groupName) return;
      this.setChecked(item, false, false);
    });
  }

  updateVisibility() {
    this.items.forEach((item) => {
      const showWhen = item.dataset.showWhen;
      const required = item.dataset.required === 'true';
      const orphan = item.dataset.showWhenOrphan === 'true' || this.isOrphanShowWhen(showWhen);
      const visible = this.isTriggerActive(showWhen, item);

      item.hidden = !visible;
      item.classList.toggle('is-hidden', !visible);

      if (!visible) {
        this.setChecked(item, false, false);
        return;
      }

      if (required && !orphan) {
        this.setChecked(item, true, true);
      }
    });

    this.syncBlockVisibility();
    this.refreshProductTotal();
  }

  syncQtyLabels() {
    const parentQty = this.getParentQuantity();

    this.items.forEach((item) => {
      const qtyEl = item.querySelector('.lisavaruste__qty');
      if (!qtyEl) return;

      const allowQtyChange = item.dataset.allowQtyChange === 'true';
      qtyEl.textContent = allowQtyChange ? `${parentQty}x` : '1x';
    });
  }

  getParentQuantity() {
    const hintaportaat = this.section?.querySelector('hintaportaat-picker');
    if (hintaportaat && typeof hintaportaat.getQuantity === 'function') {
      return hintaportaat.getQuantity();
    }
    const qtyInput = this.section?.querySelector('.js-product-form-main input[name="quantity"]');
    const value = Number(qtyInput?.value || 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  getSelectedAddons() {
    const parentQty = this.getParentQuantity();

    return this.items
      .filter((item) => !item.hidden && this.isChecked(item))
      .map((item) => {
        const allowQtyChange = item.dataset.allowQtyChange === 'true';
        const required = item.dataset.required === 'true';
        const entry = {
          id: Number(item.dataset.variantId),
          quantity: allowQtyChange ? parentQty : 1,
          allowQtyChange,
          required
        };
        return entry;
      });
  }

  async handleSubmit(event) {
    if (!this.form || event.target !== this.form) return;

    const selectedAddons = this.getSelectedAddons();
    if (!selectedAddons.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const productForm = this.form.closest('product-form');
    const submitBtn = productForm?.querySelector('[name="add"]');
    const errorMsg = productForm?.querySelector('.js-form-error');

    const formData = new FormData(this.form);
    const parentVariantId = Number(formData.get('id'));
    const parentQty = this.getParentQuantity();

    if (!parentVariantId) return;

    const qtyInput = this.form.querySelector('input[name="quantity"]');
    if (
      qtyInput
      && typeof theme.validateQuantityInput === 'function'
      && !theme.validateQuantityInput(qtyInput, { showError: true })
    ) {
      return;
    }

    const productId = this.form.dataset.productId;
    const colorGroup = String(formData.get('properties[_color_group]') || '').trim();
    const usesProductServiceGroup =
      this.form.dataset.lisapalveluProductGroup === 'true'
      || Boolean(this.closest('lisavaruste-picker'));

    // Append nested Lisäkustannukset when the current hintaportaat tier has an extra.
    const hintaportaat = this.section?.querySelector('hintaportaat-picker');
    const feeItem =
      hintaportaat && typeof hintaportaat.getNestedFeeItem === 'function'
        ? hintaportaat.getNestedFeeItem(parentVariantId)
        : theme.hintaportaat?.getNestedFeeItem?.(parentVariantId, hintaportaat);
    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
    }
    if (errorMsg) errorMsg.hidden = true;

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      if (!usesProductServiceGroup) {
        // Same artwork, same variant, same services: top up that line instead
        // of stacking a second identical one. The re-add carries the newest
        // placement and preview, so the old line goes once the new one is in.
        const addonKey = selectedAddons
          .map((addon) => addon.id)
          .sort((a, b) => a - b)
          .join('-');
        const { logoKey, line: topUp } = await theme.hintaportaat.resolveLogoTopUp({
          form: this.form,
          section: this.section,
          variantId: parentVariantId,
          addonKey,
          quantity: parentQty,
        });
        const totalQty = parentQty + Number(topUp?.quantity || 0);

        const items = [{
          id: parentVariantId,
          quantity: totalQty,
          properties: theme.hintaportaat.logoBundleProperties(logoKey, addonKey),
        }];
        selectedAddons.forEach((addon) => {
          items.push({
            id: addon.id,
            quantity: addon.allowQtyChange ? totalQty : 1,
            parent_id: parentVariantId,
            properties: {
              _service_qty_mode: addon.allowQtyChange ? 'group' : 'fixed',
              ...(addon.required ? { _lisavaruste_required: '1' } : {}),
            },
          });
        });
        if (feeItem) items.push(feeItem);

        const data = await theme.hintaportaat.postNestedCartAdd(this.form, items, {
          logoPropertyItemIndex: 0,
          copyLogoProperties: true,
        });
        if (topUp) {
          const topUpData = await theme.hintaportaat.postCartUpdate(
            { [topUp.key]: 0 },
            { withSections: true }
          );
          if (topUpData?.sections) {
            data.sections = topUpData.sections;
            data.item_count = topUpData.item_count;
          }
        }
        // Adding the same variant again merges its nested lines, so the 1x
        // add-ons and the tier fee have to be pulled back to one.
        const normalized = await theme.hintaportaat.normalizeFixedNestedLines([
          ...selectedAddons
            .filter((addon) => !addon.allowQtyChange)
            .map((addon) => addon.id),
          ...(feeItem ? [feeItem.id] : []),
        ]);
        this.renderCartResult(data, {
          productForm,
          parentVariantId,
          cartDrawer,
          serviceGroupKey: '',
          reusedServiceGroup: false,
          refreshCart: normalized,
        });
        return;
      }

      const cart = await theme.hintaportaat.fetchCart();
      const group = theme.hintaportaat.serviceGroupState(
        cart,
        productId,
        colorGroup,
        selectedAddons.map((addon) => addon.id),
        { scope: 'product' }
      );
      const reusesExistingSetup = Boolean(group.groupKey && group.exists);
      const customizer = this.section?.querySelector('logo-customizer');
      const replacesExistingSetup = reusesExistingSetup && Boolean(customizer?.logos?.length);
      const sameVariantParents = replacesExistingSetup
        ? group.parents.filter((item) => Number(item.variant_id) === parentVariantId)
        : [];
      const carriedQuantity = sameVariantParents.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      );
      const parentLineProperties = {
        ...(colorGroup ? { _color_group: colorGroup } : {}),
        ...(!reusesExistingSetup || replacesExistingSetup
          ? { _service_group_anchor: '1' }
          : {}),
      };
      const items = [{
        id: parentVariantId,
        quantity: parentQty + carriedQuantity,
        ...(Object.keys(parentLineProperties).length ? { properties: parentLineProperties } : {}),
      }];

      if (!reusesExistingSetup || replacesExistingSetup) {
        selectedAddons.forEach((addon) => {
          items.push({
            id: addon.id,
            quantity: addon.allowQtyChange ? group.totalQuantity + parentQty : 1,
            parent_id: parentVariantId,
            properties: {
              _service_group_key: group.groupKey,
              _service_product_id: String(productId || ''),
              _service_parent_title: this.form.dataset.productTitle || '',
              _service_color: colorGroup,
              _service_qty_mode: addon.allowQtyChange ? 'group' : 'fixed',
              ...(addon.required ? { _lisavaruste_required: '1' } : {}),
            },
          });
        });
      }

      if (feeItem) items.push(feeItem);

      const data = await theme.hintaportaat.postNestedCartAdd(this.form, items, {
        logoPropertyItemIndex: 0,
        copyLogoProperties: !reusesExistingSetup || replacesExistingSetup,
      });

      if (replacesExistingSetup) {
        await theme.hintaportaat.replaceServiceGroupSetup(group, parentVariantId);
      }

      await theme.hintaportaat.syncServiceGroups();

      this.renderCartResult(data, {
        productForm,
        parentVariantId,
        cartDrawer,
        serviceGroupKey: group.groupKey,
        reusedServiceGroup: reusesExistingSetup && !replacesExistingSetup,
        refreshCart: true,
      });
    } catch (error) {
      console.log(error); // eslint-disable-line
      if (errorMsg) {
        errorMsg.hidden = false;
        errorMsg.textContent = error?.message || String(error);
      }
      productForm?.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: errorMsg?.textContent }
      }));
      if (cartDrawer) cartDrawer.refresh(true);
    } finally {
      if (submitBtn) {
        submitBtn.classList.add('is-success');
        submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          submitBtn.classList.remove('is-loading');
          submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }
  }

  renderCartResult(data, options) {
    const {
      productForm,
      parentVariantId,
      cartDrawer,
      serviceGroupKey,
      reusedServiceGroup,
      refreshCart = false,
    } = options;

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      if (theme.settings.afterAtc === 'page') {
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      } else if (cartDrawer) {
        setTimeout(() => {
          if (refreshCart) {
            cartDrawer.refresh(theme.settings.afterAtc === 'drawer');
          } else {
            cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          }
        }, 700);
      }

      productForm?.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          variantId: parentVariantId,
          serviceGroupKey,
          reusedServiceGroup
        }
      }));
  }
}

if (!customElements.get('lisavaruste-picker')) {
  customElements.define('lisavaruste-picker', LisavarustePicker);
}
